import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { JOIN_PATH } from "@/config";
import { ChevronLeft, ChevronRight, ShoppingBasket, Tractor, Download } from "lucide-react";

// Renders a string with "Source2Table" styled like the header (green "2").
function withBrand(text) {
  const parts = text.split("Source2Table");
  if (parts.length === 1) return text;
  const out = [];
  parts.forEach((p, idx) => {
    if (p) out.push(<span key={`t${idx}`}>{p}</span>);
    if (idx < parts.length - 1)
      out.push(
        <span key={`b${idx}`} className="whitespace-nowrap">
          Source<span className="text-[#4A6B41]">2</span>Table
        </span>
      );
  });
  return out;
}


// Authentic app screens available in /public/showcase. Screens that don't yet
// have a real screenshot are intentionally omitted (not fabricated).
const BUYER_SLIDES = [
  {
    src: "/showcase/buyer_home.jpg",
    caption: "Your Starting Point",
    description:
      "See how your personalized home screen brings the app's main features together.",
  },
  {
    src: "/showcase/buyer_producer.jpg",
    caption: "Inside a Producer Profile",
    description:
      "Preview the business information, photos, product listings, and profile details available within the app.",
  },
  {
    src: "/showcase/buyer_directions.jpg",
    caption: "Find Your Way to Local Producers",
    description:
      "Get directions to nearby farms, markets, and producers using Google Maps.",
  },
];

const PRODUCER_SLIDES = [
  {
    src: "/showcase/producer_dashboard.jpg",
    caption: "Your Business Dashboard",
    description:
      "A look at the main controls and business management tools in your producer account.",
  },
  {
    src: "/showcase/producer_inventory.jpg",
    caption: "Manage Your Listings",
    description:
      "See the interface for adding products, editing listings, and updating stock information.",
  },
  {
    src: "/showcase/producer_messaging.jpg",
    caption: "Manage Your Conversations",
    description:
      "See how incoming messages and customer conversations appear within the app.",
  },
];

const TABS = {
  buyers: {
    heading: "Your Source2Table Experience",
    description:
      "Take a closer look at the tools you'll use to navigate the app, personalize your experience, and keep track of your favourites.",
    slides: BUYER_SLIDES,
  },
  producers: {
    heading: "Your Business at Your Fingertips",
    description:
      "Explore the dashboard and everyday tools designed to help you manage your Source2Table presence.",
    slides: PRODUCER_SLIDES,
  },
};

const btnSolid =
  "inline-flex items-center justify-center gap-2 rounded-full font-sans font-semibold tracking-wide bg-[#4A6B41] text-[#F9F6F0] px-7 py-3 text-sm sm:text-base shadow-lg shadow-[#4A6B41]/20 hover:bg-[#2F4829] transition-transform duration-200 hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4A6B41]/30";

const Carousel = ({ slides, tabKey }) => {
  const [i, setI] = useState(0);
  const touchX = useRef(null);
  const count = slides.length;

  const go = (next) => setI((cur) => (next + count) % count);
  const onTouchStart = (e) => (touchX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? i + 1 : i - 1);
    touchX.current = null;
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); go(i + 1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); go(i - 1); }
  };

  const slide = slides[i];

  return (
    <div
      className="mt-8 flex flex-col items-center"
      role="group"
      aria-roledescription="carousel"
      aria-label={`${tabKey} app screenshots`}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div className="relative flex items-center justify-center gap-4 sm:gap-8 w-full">
        <button
          data-testid={`${tabKey}-carousel-prev`}
          onClick={() => go(i - 1)}
          aria-label="Previous screenshot"
          className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-[#D8E0D5] text-[#2F4829] shadow-sm hover:bg-[#4A6B41] hover:text-[#F9F6F0] transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4A6B41]/30"
        >
          <ChevronLeft size={22} />
        </button>

        <div
          className="w-[230px] sm:w-[260px] shrink-0"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="rounded-[2.2rem] p-2.5 bg-[#1C241B] shadow-2xl ring-1 ring-black/10">
            <img
              key={slide.src}
              src={slide.src}
              alt={slide.caption}
              loading="lazy"
              decoding="async"
              className="w-full rounded-[1.7rem] block select-none"
              style={{ aspectRatio: "390 / 844", objectFit: "cover" }}
              draggable={false}
            />
          </div>
        </div>

        <button
          data-testid={`${tabKey}-carousel-next`}
          onClick={() => go(i + 1)}
          aria-label="Next screenshot"
          className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-[#D8E0D5] text-[#2F4829] shadow-sm hover:bg-[#4A6B41] hover:text-[#F9F6F0] transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4A6B41]/30"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      <div className="mt-6 text-center max-w-md min-h-[84px]">
        <p className="font-sans font-extrabold text-lg text-[#1C241B]">{slide.caption}</p>
        <p className="mt-1.5 text-sm text-[#5A6757] leading-relaxed">{slide.description}</p>
      </div>

      <div className="mt-4 flex items-center gap-2.5" role="tablist" aria-label="Choose screenshot">
        {slides.map((s, idx) => (
          <button
            key={s.src}
            data-testid={`${tabKey}-dot-${idx}`}
            onClick={() => setI(idx)}
            aria-label={`Go to screenshot ${idx + 1}`}
            aria-current={i === idx}
            className={`h-2.5 rounded-full transition-all ${
              i === idx ? "w-7 bg-[#4A6B41]" : "w-2.5 bg-[#4A6B41]/30 hover:bg-[#4A6B41]/50"
            }`}
          />
        ))}
      </div>

      {/* Mobile prev/next (under dots) */}
      <div className="mt-5 flex sm:hidden items-center gap-3">
        <button
          onClick={() => go(i - 1)}
          aria-label="Previous screenshot"
          className="h-10 w-10 flex items-center justify-center rounded-full bg-white border border-[#D8E0D5] text-[#2F4829]"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => go(i + 1)}
          aria-label="Next screenshot"
          className="h-10 w-10 flex items-center justify-center rounded-full bg-white border border-[#D8E0D5] text-[#2F4829]"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export const AppShowcase = () => {
  const [tab, setTab] = useState("buyers");
  const active = TABS[tab];

  return (
    <section
      id="inside-the-app"
      data-testid="app-showcase-section"
      className="px-4 sm:px-6 lg:px-12 py-20 md:py-28 bg-[#F0F4EE] border-y border-[#4A6B41]/10"
    >
      <div className="mx-auto max-w-5xl">
        <div className="text-center">
          <h2 className="font-sans font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1C241B] tracking-tight">
            Take a Look Inside{" "}
            <span className="whitespace-nowrap">
              Source<span className="text-[#4A6B41]">2</span>Table
            </span>
          </h2>
          <p className="mt-3 font-sans text-base sm:text-lg text-[#4A6B41] font-semibold">
            Explore the app before you join.
          </p>
          <p className="mt-4 mx-auto max-w-xl text-sm sm:text-base text-[#5A6757] leading-relaxed">
            Get a firsthand look at the screens, features, and tools designed for
            buyers and producers. Select your experience below to explore
            Source2Table.
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex justify-center">
          <div
            role="tablist"
            aria-label="Choose experience"
            className="grid grid-cols-2 gap-2 p-1.5 rounded-full bg-white border border-[#D8E0D5] shadow-sm w-full max-w-sm"
          >
            {[
              { key: "buyers", label: "For Buyers", Icon: ShoppingBasket },
              { key: "producers", label: "For Producers", Icon: Tractor },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                data-testid={`showcase-tab-${key}`}
                onClick={() => setTab(key)}
                className={`inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4A6B41]/30 ${
                  tab === key ? "bg-[#4A6B41] text-[#F9F6F0]" : "text-[#2F4829]"
                }`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab panel */}
        <div className="mt-10 text-center">
          <h3 className="font-sans font-extrabold text-2xl text-[#1C241B]">{withBrand(active.heading)}</h3>
          <p className="mt-2 mx-auto max-w-xl text-sm sm:text-base text-[#5A6757] leading-relaxed">
            {active.description}
          </p>
        </div>

        <Carousel key={tab} slides={active.slides} tabKey={tab} />

        {/* CTA */}
        <div className="mt-10 flex justify-center">
          {tab === "buyers" ? (
            <Link data-testid="showcase-get-app-btn" to={JOIN_PATH} className={btnSolid}>
              <Download size={18} /> Get the App
            </Link>
          ) : (
            <Link data-testid="showcase-join-producer-btn" to="/join" className={btnSolid}>
              <Tractor size={18} /> Join as a Producer
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
